import { useNavigate } from "@solidjs/router";
import { type Component, createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { refreshInCardHead, refreshSlot } from "./admin/refreshSlot";
import { archiveSlugForSelection } from "./lib/archiveContext";
import { archivedUnread } from "./lib/archiveRollup";
import { activeAudio, playerHidden, showPlayer } from "./lib/audioPlayer";
import { type ChannelKey, channelKey } from "./lib/channelKey";
import { conversationMuteKey, isConversationMuted } from "./lib/conversationMute";
import { createDismissOnOutsidePointer } from "./lib/dismissOnOutsidePointer";
import { syncedSetChannelPresencePref } from "./lib/displayPrefs";
import { canDetach, confirmDetach, confirmQuit } from "./lib/lifecycle";
import { membersByChannel } from "./lib/members";
import { mentionsBundleBySlug } from "./lib/mentionsWindow";
import { spaceAbove } from "./lib/menuPosition";
import {
  type MobilePanelSetters,
  openAdminPanel,
  openArchivePanel,
  openHomePanel,
  openListPanel,
  openMentionsPanel,
  openSettingsPanel,
  openThemesPanel,
} from "./lib/mobilePanel";
import { MUTE_SNOOZE_OPTIONS, type MuteSnoozeValue, snoozeUntil } from "./lib/muteSnooze";
import { isAdmin } from "./lib/networks";
import {
  applyConversationMute,
  clearConversationMute,
  notificationPrefs,
} from "./lib/notificationPrefs";
import { createOverlayLock } from "./lib/overlayScrollLock";
import { channelPresenceVisible } from "./lib/presenceFilter";
import { openRadioPicker } from "./lib/radio";
import { selectedChannel, setSelectedChannel } from "./lib/selection";
import {
  ADMIN_WINDOW_NAME,
  ADMIN_WINDOW_SLUG,
  HOME_WINDOW_NAME,
  HOME_WINDOW_SLUG,
  LIST_WINDOW_NAME,
} from "./lib/windowKinds";

// #473 → #500 — RailActions: EVERY rail affordance (home · rooms · radio ·
// themes · archive · settings · admin · denoise) lives here, mounted by BOTH
// branches of Shell's `isMobile()` split — one component, one place, same
// buttons on desktop and mobile.
//
// #500 — the actions are COLLAPSED behind ONE launcher permanently pinned at
// the bottom of the rail; tapping it expands them in a menu that OVERLAYS the
// nick area. Before #500 they were an always-expanded column: on a big channel
// the nick list overflowed and pushed the column below the fold (desktop:
// unreachable) / squeezed the list (mobile). The launcher never shares vertical
// flow with the list, so it is always reachable; the expanded menu overlays
// rather than displaces, so it costs no permanent vertical space. The other
// half of the fix is the sibling `.members-pane`-owns-the-scroll CSS (so the
// pinned launcher stays in view while a long list scrolls internally, on
// desktop as it already did on mobile).
//
// #1040 — HOME is an exception, and it is an exception to the REASON, not to
// the rule. #500's cost was a nick-list cost; `RailContext` renders nothing for
// home, so that window's rail is an empty column with one launcher pinned to
// its bottom, charging a tap to reach buttons that had nowhere to be pushed to.
// On home the actions therefore lay out EXPANDED and in flow from the top of
// the rail, and the launcher is not rendered at all.
//
// #1073 — ADMIN is the second, on the same reason and not by analogy:
// `RailContext` has no admin arm either and `MembersPane` is joined-channel
// gated, so that rail is as empty above the actions as home's. It also matters
// more there — the console's close × was deleted, so this menu is the way out
// of it. Every OTHER kind keeps #500 verbatim: the launcher stays the single
// door everywhere else.
//
// The expanded state is not "the overlay, left open": the popover geometry
// (absolute, `bottom: 100%`, a max-height measured from the space above the
// launcher) exists to float over a list, and it is switched off in CSS via the
// `.rail-actions.expanded` container class. The dismiss machinery is switched
// off with it — no overlay refcount, no Escape, no outside-click — because a
// permanent column that Escape can close is a column the operator cannot bring
// back, and a refcount held for the life of a window freezes the scrollback
// snapshot behind it (#608).
//
// Menu open state is a plain local signal — ephemeral UI state, like Shell's
// `membersOpen` / `settingsOpen`. cic-never-originates-state governs IRC WINDOW
// state (join/part/kick), NOT a client-local drawer toggle.
//
// Dismiss REUSES the shared overlay verb `createOverlayLock`: Escape joins the
// ordered ESC-close stack (keybindings → runTopmostOverlayEscape), and the
// refcount scroll-lock keeps the rail's `touch-action: pan-y` contract intact
// while the menu overlays the members list on mobile (the exact #500 caveat).
// Outside-click closes via a NON-blocking document `pointerdown` listener — NOT
// a covering backdrop scrim: a click outside the rail closes the menu AND still
// reaches its target, so a tap on a sidebar channel / compose selects it in one
// gesture. A full-viewport scrim (the modal family's idiom) would swallow that
// first click — wrong for a lightweight rail popover.
//
// Every launcher closes the menu after firing (single-shot, like every
// overlay) — including the two lifecycle entries, which hand the operator to
// a modal and must not leave a live menu waiting underneath it for the
// Cancel. `denoise` does NOT close it — it is a per-channel state toggle the
// operator flips in place (watching the accent flip, re-toggling), not a
// navigation away.
//
// Buttons, in order: home · rooms · mentions · radio · themes · archive ·
// settings · admin · refresh · denoise · detach · quit. Each carries its NAME as visible
// text next to the glyph (#473: bare emoji had to be guessed / long-pressed).
//
// Gating is CAPABILITY-only — no form-factor gates (#473):
//   * home / radio / themes / settings / archive / quit — always. The radio
//     picker's station table is baked in (#682), so there is nothing to gate
//     it on; it opens RailRadio, which overlays this same rail.
//   * rooms — needs a network context (`archiveSlugForSelection()`).
//   * mentions — needs a bundle to re-open for that network context.
//   * admin — `isAdmin()`.
//   * refresh — #1073, the active admin tab's re-fetch, registered on
//     `admin/refreshSlot`. Present only while the console is open, only for a
//     tab that has a fetch to repeat, and only where that seam says the rail
//     is the button's home (the phone; on desktop it renders in the data
//     card's own head instead).
//   * denoise — channel-gated (a channel window is selected).
//   * detach — `canDetach()`: a persistent identity has a bouncer to leave
//     running, an ephemeral visitor does not.
//
// #986 — three arrivals, and the two lifecycle verbs among them are why this
// menu now carries a destructive class of action:
//
//   * `@` mentions moved OFF `.shell-chrome`, the band #985 removes. It was
//     gated `isMobile() && bundle` there, because on desktop it would have
//     duplicated the Sidebar mentions row (#71 INC-2). That gate does NOT
//     travel with it: #473 already settled that the rail carries the same set
//     on both form factors, and `home` is the standing precedent — a sidebar
//     row AND a rail launcher, deliberately. A form-factor gate here would be
//     the one thing this component says it does not do.
//   * `detach` / `quit` moved out of the settings drawer, each behind the
//     shared #195 confirm modal via lib/lifecycle's `confirmDetach` /
//     `confirmQuit`. ONE confirm paradigm: the drawer's two-tap
//     `InlineConfirmButton` arm is NOT reproduced here (the component keeps
//     serving its ~20 other call sites unchanged — only its use as a
//     lifecycle-verb gate ends). The modal body is subject-TRUE; the copy
//     block in lib/lifecycle.ts records why one sentence could not honestly
//     serve three different events.
//
// The lifecycle pair sits LAST, after the navigation set — the conventional
// slot for a destructive verb, and one the confirm modal makes affordable.
//
// #473 — `archive` is ALWAYS shown, like settings — NOT selection-gated: the
// grouped `ArchiveModal` is the SINGLE archive surface and must be reachable
// from home / mentions / admin (no network context) too. rooms stays
// selection-gated because it navigates to a per-network `$list` window.
//
// The window-nav launchers (home / rooms / admin) and the own-signal launchers
// (settings / themes / archive) route through the SAME `lib/mobilePanel` mutex
// helpers, so the members | settings | archive | none invariant is untouched.

export type Props = {
  /**
   * The three Shell-local signals the mobilePanel mutex helpers wrap. The only
   * prop this drawer needs — every other input (selection, isAdmin, network
   * context, presence pref) is read directly from its store, matching the
   * house style (ShellChrome / the former ActionCluster read stores directly).
   */
  setters: MobilePanelSetters;
};

// #588 — px kept clear above the menu so the topmost row isn't flush against
// whatever is above it. Breathing room ONLY: #913 established that clearing the
// notch / status bar is a separate, ~59px job that this constant was silently
// being asked to do. The inset is subtracted in CSS (see below); this is what
// remains on top of it. Fed to `spaceAbove`.
const RAIL_MENU_TOP_GAP = 8;

// #950 — the picker's one non-duration row. Outside `MuteSnoozeValue` on
// purpose: unmuting is not a snooze offer, it is the inverse verb, and giving
// it a member of that union would let a caller ask `snoozeUntil` for it.
const UNMUTE_VALUE = "off";

const RailActions: Component<Props> = (props) => {
  // #986 — the two lifecycle verbs land the operator on /login once the
  // teardown resolves. `logout()` nulls the token and main.tsx's RequireAuth
  // would redirect on its own, but the explicit navigation makes the landing
  // deterministic instead of effect-ordered (the shape SettingsDrawer used
  // before these buttons moved here).
  const navigate = useNavigate();
  const toLogin = (): void => navigate("/login", { replace: true });

  // #188 item 6 / #986 — which network's mentions bundle can be re-opened?
  // Derived from the current selection exactly as `rooms` derives its network
  // (`archiveSlugForSelection`), and null unless that network HAS a bundle:
  // there is nothing to re-open otherwise. Returns null while the mentions
  // panel is itself selected, which correctly hides the redundant entry.
  const mentionsSlug = (): string | null => {
    const slug = archiveSlugForSelection();
    if (slug === null) return null;
    return mentionsBundleBySlug()[slug] ? slug : null;
  };

  // The channel this rail is currently showing, or null on non-channel windows
  // — drives the channel-gated denoise toggle (any channel: the toggle writes a
  // pref that persists to reconnect, so it is meaningful on parked channels
  // too). Mirrors the former Shell `railChannel` memo, now owned here.
  const channel = (): { networkSlug: string; channelName: string } | null => {
    const sel = selectedChannel();
    return sel && sel.kind === "channel"
      ? { networkSlug: sel.networkSlug, channelName: sel.channelName }
      : null;
  };

  // #950 — the CONVERSATION this rail is showing: a channel or a query, the
  // two window kinds a mute can be keyed on. Wider than `channel()` above by
  // exactly one kind, and deliberately a separate memo: denoise is a
  // per-CHANNEL presence pref (a query has no join/part traffic), the mute is
  // per-conversation.
  const conversation = (): { networkSlug: string; channelName: string } | null => {
    const sel = selectedChannel();
    return sel && (sel.kind === "channel" || sel.kind === "query")
      ? { networkSlug: sel.networkSlug, channelName: sel.channelName }
      : null;
  };

  // #1040 — home is a kind where the actions are not collapsed. #500's cost was
  // a NICK LIST cost (the column pushed the list below the fold), and
  // `RailContext` renders nothing for home, so on this window there is no list
  // to protect and the launcher charges a tap for nothing. Derived from the
  // selection rather than held as a second signal: "which kind am I showing" is
  // state that already exists.
  //
  // #1073 — admin joins it on the same test, not by analogy: `RailContext` has
  // no admin arm either, and `MembersPane` is gated on
  // `isActiveChannelJoined()`, so the admin window's rail is as empty above the
  // actions as home's. It also carries more weight here than on home — the
  // console's close × was deleted, so this menu IS the way out of it.
  const expanded = (): boolean => {
    const kind = selectedChannel()?.kind;
    return kind === "home" || kind === "admin";
  };

  // #500 — collapsible-menu open state (ephemeral UI-local, see moduledoc).
  const [open, setOpen] = createSignal(false);
  const close = (): void => {
    setOpen(false);
  };
  // #1040 — a menu opened on a channel is retired on arrival at an expanded
  // kind (home, and admin since #1073). Every
  // rail action already closes it, and an outside pointerdown closes it too,
  // but neither covers a selection change the rail did not cause (the #986
  // demote redirect lands on home on its own). Without this the transient
  // menu's overlay refcount would stay pushed for as long as the operator sits
  // on home — a permanent column holding a scroll-lock, which is what freezes
  // ScrollbackPane's snapshot (#608) — and the menu would be handed back
  // already open on the way out. One reset at the source beats gating each of
  // the three popover mechanisms below on `!expanded()` separately.
  createEffect(() => {
    if (expanded()) setOpen(false);
  });
  // Reuse the shared overlay verb: Escape close + refcount scroll-lock (keeps
  // the rail's touch-action/pan-y contract while the menu overlays the list).
  createOverlayLock(() => open(), ".rail-actions-menu", close);
  // #500 — outside-click dismiss via a NON-blocking pointerdown listener (not a
  // covering backdrop scrim): a click outside the rail closes the menu AND still
  // reaches its target, so tapping a sidebar channel / compose / hamburger
  // selects it in one gesture instead of being swallowed by a full-viewport
  // scrim. `rootRef` is the `.rail-actions` box (launcher + menu live inside it),
  // so clicks on the launcher/menu never self-close here — the launcher toggles,
  // the action buttons call close() themselves. Registered only while open; the
  // opening click already fired before the effect runs, so it can't self-close.
  // #682 — the listener itself moved to `lib/dismissOnOutsidePointer`, where
  // the second rail popover (the radio picker) shares it. The reasoning above
  // — non-blocking listener, NOT a scrim — travelled with it and now lives on
  // the verb; nothing about the behaviour here changed.
  let rootRef: HTMLDivElement | undefined;
  createDismissOnOutsidePointer(open, () => rootRef, close);

  // #588 — the menu opens UPWARD from the launcher (`bottom: 100%`), so its
  // usable height is ONLY the space above `rootRef` (the `.rail-actions`
  // container the absolute menu anchors to), NOT the whole viewport. The CSS
  // `max-height: var(--viewport-height)` capped it at the viewport, so on a
  // short viewport the overflowing rows grew off the top of the screen with no
  // scroll (menu shorter than its own oversized cap → `overflow-y: auto` never
  // engaged) and the top actions were unreachable. Measure the real space above
  // on open and cap `max-height` to it via the shared `menuPosition` seam
  // (`spaceAbove`) — the same clamp `UserContextMenu` uses, rather than a
  // second, differently-buggy CSS-only guess. Re-measure on `resize` /
  // `visualViewport` resize: that is where the mobile-keyboard-up case lives
  // (the visual viewport shrinks, the launcher rides up, the space above
  // changes) — and where rotation lands too, which matters now that the cap's
  // other operand (the safe-area inset) also changes with orientation. Null
  // while closed → the menu isn't mounted anyway, and the CSS var() chain
  // falls back to the viewport height.
  //
  // #913 — this measurement is only HALF the cap. `getBoundingClientRect()` is
  // relative to the layout viewport, whose origin under `viewport-fit=cover` is
  // the physical top of the display, behind the status bar. The stylesheet
  // takes `var(--safe-area-inset-top)` off what we publish here; we do NOT read
  // the inset in JS, because `getComputedStyle().getPropertyValue()` on an
  // unregistered custom property is not guaranteed to resolve `env()` to a
  // length — it can return the token stream, and the NaN that follows would
  // fail silently as a no-op fix.
  const [menuSpaceAbove, setMenuSpaceAbove] = createSignal<number | null>(null);
  createEffect(() => {
    if (!open()) {
      setMenuSpaceAbove(null);
      return;
    }
    const measure = (): void => {
      if (!rootRef) return;
      setMenuSpaceAbove(spaceAbove(rootRef.getBoundingClientRect().top, RAIL_MENU_TOP_GAP));
    };
    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    onCleanup(() => {
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    });
  });

  return (
    // biome-ignore lint/a11y/useSemanticElements: role="group" gives the button cluster an accessible name; biome suggests <fieldset>, a form-control grouping (needs <legend>, paints a border) — wrong for a rail toolbar of action buttons.
    <div
      class="rail-actions"
      classList={{ expanded: expanded() }}
      role="group"
      aria-label="window actions"
      ref={rootRef}
    >
      <Show when={expanded() || open()}>
        {/* #500 — the expanded menu overlays the nick area above the launcher.
            Holds every action, unchanged. `createOverlayLock` targets this
            element's selector; outside-click dismiss is the pointerdown listener
            above (no covering scrim).
            #1040 — on home the SAME element is the rail's own content: the
            `.expanded` class on the container above takes the absolute /
            upward / capped geometry back off it (default.css). */}
        <div
          class="rail-actions-menu"
          // #1040 — `menu` describes a popup a menubutton owns. On home there
          // is no launcher and nothing pops up, so the role would be an orphan
          // claim; the container's `role="group" aria-label="window actions"`
          // already names the cluster.
          role={expanded() ? undefined : "menu"}
          style={
            // #588/#913 — publish the JS-measured space above the launcher; the
            // stylesheet's `max-height` subtracts the safe-area inset from it
            // and clamps. undefined → the var() falls back to the viewport
            // height (pre-measure only).
            menuSpaceAbove() !== null
              ? { "--rail-menu-space-above": `${menuSpaceAbove()}px` }
              : undefined
          }
        >
          {/* #291 — home launcher. Always visible; leftmost/topmost. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-home"
            aria-label="open home"
            data-testid="mobile-panel-home"
            onClick={() => {
              openHomePanel(props.setters, () =>
                setSelectedChannel({
                  networkSlug: HOME_WINDOW_SLUG,
                  channelName: HOME_WINDOW_NAME,
                  kind: "home",
                }),
              );
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{1F3E0}"}
            </span>
            <span class="rail-action-label">home</span>
          </button>

          {/* #361 — rooms launcher (channel directory / $list). Gated on a
              network context; labelled `rooms` (#473 naming note), testid kept
              as `mobile-panel-list` so existing tests keep pointing at a real
              thing. */}
          <Show when={archiveSlugForSelection()}>
            {(slug) => (
              <button
                type="button"
                class="shell-chrome-btn rail-action rail-action-rooms"
                aria-label="open rooms"
                data-testid="mobile-panel-list"
                onClick={() => {
                  openListPanel(props.setters, () =>
                    setSelectedChannel({
                      networkSlug: slug(),
                      channelName: LIST_WINDOW_NAME,
                      kind: "list",
                    }),
                  );
                  close();
                }}
              >
                <span class="rail-action-icon" aria-hidden="true">
                  {"\u{1F4C7}"}
                </span>
                <span class="rail-action-label">rooms</span>
              </button>
            )}
          </Show>

          {/* #986 — mentions launcher (@). The ONE door back into a network's
              "you were /away" bundle now that `.shell-chrome` is losing its
              copy (#985). Gated on there BEING a bundle for the current
              network context — capability, not form factor (see moduledoc).
              Routes through the same nav mutex as home / rooms / admin. */}
          <Show when={mentionsSlug()}>
            {(slug) => (
              <button
                type="button"
                class="shell-chrome-btn rail-action rail-action-mentions"
                aria-label="open mentions"
                data-testid="rail-action-mentions"
                onClick={() => {
                  openMentionsPanel(props.setters, () =>
                    setSelectedChannel({
                      networkSlug: slug(),
                      channelName: "",
                      kind: "mentions",
                    }),
                  );
                  close();
                }}
              >
                {/* #1076 — 📣, not `@`. The icon slot is an emoji slot: a
                    bare ASCII glyph renders in the text font at the text
                    font's advance width, so the label after it starts
                    somewhere no other label starts. 🔔 was unavailable — it
                    is already the mute toggle's unmuted state below. */}
                <span class="rail-action-icon" aria-hidden="true">
                  {"\u{1F4E3}"}
                </span>
                <span class="rail-action-label">mentions</span>
              </button>
            )}
          </Show>

          {/* #1697 — the door back to a HIDDEN transport. `AudioMiniPlayer`
              can now be taken off screen while it keeps playing, and this is
              what restores it.
              WHY HERE. It has to be general: an upload passes `label: null`
              and is in no station table, so the rail's `rail-radio-now` chrome
              (gated on `tunedStation()`) cannot be the door — whatever
              restores the bar is keyed on the PLAYER, not on the radio. And it
              has to cost no vertical space, which is the entire point of
              hiding: a restore handle left in the docked slot would still owe
              the 44px HIG floor, giving back almost none of the room the
              operator hid the bar to reclaim. This drawer already carries
              zero permanent cost (#500) and already hosts `radio`.
              Gated on there being something to restore, the same
              CAPABILITY gate the mentions launcher above uses. Closes the menu
              like every launcher that navigates away: what it reveals is
              behind this drawer. */}
          <Show when={activeAudio() !== null && playerHidden()}>
            {(_present) => (
              <button
                type="button"
                class="shell-chrome-btn rail-action rail-action-show-player"
                /* The label names the source when it has one: hidden, this row
                   inherits the job the docked bar's caption did on mobile
                   (#682), which is answering "what am I listening to". An
                   upload has no title on the wire, so it says nothing rather
                   than inventing one. */
                aria-label={
                  activeAudio()?.label === null || activeAudio()?.label === undefined
                    ? "show player"
                    : `show player — ${activeAudio()?.label}`
                }
                data-testid="rail-action-show-player"
                onClick={() => {
                  showPlayer();
                  close();
                }}
              >
                <span class="rail-action-icon" aria-hidden="true">
                  {"\u{1F39A}"}
                </span>
                <span class="rail-action-label">player</span>
              </button>
            )}
          </Show>

          {/* #682 — radio launcher. Opens the station picker, which overlays
              this same rail (RailRadio, mounted just above this drawer). It
              closes the menu like every other launcher: the picker takes over
              the rail, so leaving this menu live underneath it would stack two
              overlays on one column. ALWAYS shown — the station table is baked
              in, so there is no capability to gate on. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-radio"
            aria-label="open radio"
            data-testid="rail-action-radio"
            onClick={() => {
              openRadioPicker();
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{1F4FB}"}
            </span>
            <span class="rail-action-label">radio</span>
          </button>

          {/* #75/#332 — themes launcher: opens the settings drawer on the themes
              sub-page (openThemesPanel deep-links via settingsNav). Always. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-themes"
            aria-label="open themes"
            data-testid="mobile-panel-themes"
            onClick={() => {
              openThemesPanel(props.setters);
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{1F3A8}"}
            </span>
            <span class="rail-action-label">themes</span>
          </button>

          {/* #473 — archive launcher. ALWAYS shown (like settings), NOT
              selection-gated: opens the ONE grouped ArchiveModal via the shared
              archive mutex helper. testid kept as `mobile-panel-archive`. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-archive"
            aria-label="open archive"
            data-testid="mobile-panel-archive"
            onClick={() => {
              openArchivePanel(props.setters);
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{1F4C2}"}
            </span>
            <span class="rail-action-label">archive</span>
            {/* issue 2096 — the rollup of what is unread BEHIND this door.
                Same two badge classes the sidebar and the modal's own rows
                use, so messages and events stay distinguishable instead of
                collapsing into one dot. Derived from the `/me` seed cic
                already holds (lib/archiveRollup.ts) — no fetch, and no
                eager archive load: `loadArchive` stays lazy per group. */}
            <Show when={archivedUnread().messages > 0 || archivedUnread().events > 0}>
              <span class="rail-action-unread" data-testid="rail-archive-unread">
                <Show when={archivedUnread().messages > 0}>
                  <span class="sidebar-msg-unread">{archivedUnread().messages}</span>
                </Show>
                <Show when={archivedUnread().events > 0}>
                  <span class="sidebar-events-unread">{archivedUnread().events}</span>
                </Show>
              </span>
            </Show>
          </button>

          {/* #71 INC-2 — settings cog. ALWAYS rendered; the cluster-wide
              "settings reachable from every window kind" rule. testid +
              aria-label kept verbatim: many e2e specs locate it via
              getByLabel(/open settings/i) and the `action-cluster-cog` testid. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-cog"
            aria-label="open settings"
            data-testid="action-cluster-cog"
            onClick={() => {
              openSettingsPanel(props.setters);
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{2699}\u{FE0F}"}
            </span>
            <span class="rail-action-label">settings</span>
          </button>

          {/* UX-6 bucket C — admin launcher. isAdmin()-gated (capability, not
              form factor). Selection-driven: Shell mounts AdminPane on kind
              "admin". */}
          <Show when={isAdmin()}>
            <button
              type="button"
              class="shell-chrome-btn rail-action rail-action-admin"
              aria-label="open admin"
              data-testid="mobile-panel-admin"
              onClick={() => {
                openAdminPanel(props.setters, () =>
                  setSelectedChannel({
                    networkSlug: ADMIN_WINDOW_SLUG,
                    channelName: ADMIN_WINDOW_NAME,
                    kind: "admin",
                  }),
                );
                close();
              }}
            >
              <span class="rail-action-icon" aria-hidden="true">
                {"\u{1F527}"}
              </span>
              <span class="rail-action-label">admin</span>
            </button>
          </Show>

          {/* #1073 — the active admin tab's refresh. vjt: *"il refresh può
              tranquillamente stare tra le actions nel rail, non serve così
              prominente"*. It used to be one of two controls in the console's
              own band, which is more prominence than a re-fetch earns.

              The rail knows nothing about admin tabs: `refreshSlot` is the
              module singleton a tab registers itself on while mounted, so the
              row appears only for a tab that HAS something to re-fetch (Events
              has no fetch and correctly contributes nothing) and disappears
              with the console. It carries the tab's own testid, unchanged by
              the move: `admin-visitors-refresh` and its siblings are what the
              tabs' own suites assert the registration publishes, and renaming
              a control while relocating it hides which of the two broke.

              `refreshInCardHead()` is the desktop placement, next to the data
              rather than in the window furniture; rendering here as well would
              put a duplicate `admin-*-refresh` in the DOM. One button either
              way, which is the seam's whole contract. */}
          <Show when={!refreshInCardHead() && refreshSlot()}>
            {(reg) => (
              <button
                type="button"
                class="shell-chrome-btn rail-action rail-action-refresh"
                aria-label={reg().label}
                aria-busy={reg().busy()}
                data-testid={reg().testId}
                onClick={() => {
                  reg().onRefresh();
                  // The rail is a drawer OVER the pane on a phone — this is
                  // mobile-only by the gate above — so leaving it up would hide
                  // the table the operator just asked to re-fetch. Unlike
                  // denoise, which flips a pref in place, the whole point of
                  // this action is the result behind the drawer.
                  props.setters.setMembersOpen(false);
                  close();
                }}
              >
                <span class="rail-action-icon" aria-hidden="true">
                  {"\u{21BB}"}
                </span>
                <span class="rail-action-label">refresh</span>
              </button>
            )}
          </Show>

          {/* #222 — per-channel join/part/quit/nick-change suppression toggle
              (denoise), widened to channel MODE rows in #1262 — the label says
              so, because the operator must know a `+b` set while denoised is
              not in the transcript. Channel-gated. One tap writes an EXPLICIT pref
              ("show"/"hide") which by the precedence rule WINS over the
              member-count size default, so it pins the channel regardless of
              size. Reading channelPresenceVisible (tracks the pref signal) keeps
              the icon/accent reactive; memberCount feeds the size-default arm.
              Does NOT close the menu (#500): a state toggle flipped in place,
              not a navigation away. #473 gave it its first visible name:
              "denoise". */}
          <Show when={channel()}>
            {(ch) => {
              const key = () => channelKey(ch().networkSlug, ch().channelName);
              const memberCount = (): number => (membersByChannel()[key()] ?? []).length;
              const presenceShown = (): boolean => channelPresenceVisible(key(), memberCount());
              const togglePresence = (): void =>
                // #449 — local apply + server PUT so the pin converges across devices.
                syncedSetChannelPresencePref(key(), presenceShown() ? "hide" : "show");
              return (
                <button
                  type="button"
                  class="shell-chrome-btn rail-action rail-action-presence-toggle"
                  classList={{ "presence-hidden": !presenceShown() }}
                  data-testid="presence-toggle"
                  aria-pressed={!presenceShown()}
                  title={
                    presenceShown()
                      ? "Hide join/part/quit/mode for this channel"
                      : "Show join/part/quit/mode for this channel"
                  }
                  aria-label="denoise join/part/quit/mode signalling"
                  onClick={togglePresence}
                >
                  <span class="rail-action-icon" aria-hidden="true">
                    {presenceShown() ? "\u{1F441}" : "\u{1F648}"}
                  </span>
                  <span class="rail-action-label">denoise</span>
                </button>
              );
            }}
          </Show>

          {/* #950 — the mute / snooze picker. #866 shipped the storage, both
              expiry readers and the tests, and NOTHING ever wrote a non-null
              `until`: the snooze was unreachable. This is the door.

              Gated on a selected CONVERSATION — channel OR query — because the
              mute is keyed on the conversation and for a DM that key is the
              PEER (an inbound DM is stored with `channel = own_nick`, so
              keying on a row's channel would collapse every DM onto one
              entry). On a query window `channelName` IS the peer nick, so ONE
              expression serves both kinds, exactly as `windowMuteKey` does for
              the sidebar projection.

              A <select> rather than a row of buttons: the offers are a small
              closed set, and a native control gives the iOS wheel / Android
              dialog for free inside a menu whose height is already capped
              (#588). Its CLOSED-state appearance is the app-wide form-control
              rule — deliberately NOT re-implemented here (see #963/PR #965).

              Unlike `denoise` next to it, this CLOSES the menu: a toggle is
              flipped in place and watched, a snooze is a choice that resolves
              and is done. The unmute row appears only when this conversation
              is muted, so the one door can also undo itself; the settings
              drawer keeps the GLOBAL list of every mute.

              The write rejects on failure and there is no banner surface in a
              menu that just closed, so a failed PUT is logged — same posture
              as the sibling denoise PUT. */}
          <Show when={conversation()}>
            {(conv) => {
              const key = (): ChannelKey =>
                conversationMuteKey(conv().networkSlug, conv().channelName);
              const isMuted = (): boolean =>
                isConversationMuted(notificationPrefs().muted_targets, key());
              const onPick = (picked: string): void => {
                if (picked === "") return;
                const write =
                  picked === UNMUTE_VALUE
                    ? clearConversationMute(key())
                    : applyConversationMute(
                        key(),
                        snoozeUntil(picked as MuteSnoozeValue, new Date()),
                      );
                write.catch((e: unknown) => console.warn("mute: PUT failed", e));
                close();
              };
              return (
                <label
                  class="shell-chrome-btn rail-action rail-action-mute"
                  data-testid="rail-action-mute"
                >
                  <span class="rail-action-icon" aria-hidden="true">
                    {isMuted() ? "\u{1F507}" : "\u{1F514}"}
                  </span>
                  <span class="rail-action-label">{isMuted() ? "muted" : "mute"}</span>
                  <select
                    class="rail-action-mute-select"
                    aria-label="mute this conversation"
                    data-testid="rail-mute-picker"
                    // Resets to the placeholder after every pick so the same
                    // offer can be chosen twice in a row (re-snoozing).
                    value=""
                    onChange={(e) => {
                      const el = e.currentTarget as HTMLSelectElement;
                      const picked = el.value;
                      el.value = "";
                      onPick(picked);
                    }}
                  >
                    <option value="">{isMuted() ? "change…" : "silence…"}</option>
                    <For each={MUTE_SNOOZE_OPTIONS}>
                      {(option) => <option value={option.value}>{option.label}</option>}
                    </For>
                    <Show when={isMuted()}>
                      <option value={UNMUTE_VALUE}>unmute now</option>
                    </Show>
                  </select>
                </label>
              );
            }}
          </Show>

          {/* #986 — detach: leave cic, KEEP the bouncer running. Offered to a
              persistent identity only, via lib/lifecycle's `canDetach()` —
              the SAME `isPersistentIdentity` question `quit()` routes on, so
              the affordance and the teardown cannot drift. The confirm modal
              is what makes this a rail entry rather than the drawer's
              unconfirmed button: it says what stays up before the tap.

              #1705 — the entry READS "switch account", not "detach". The verb
              is still `detach` everywhere in code (and the class/testid keep
              that name): what changed is the axis the LABEL is named on. The
              two lifecycle entries were both named after what happens to the
              bouncer, which is the right axis for the consequence and the
              wrong one for the intent "I want to be someone else here" — so a
              user looking to change account scanned the menu for a word that
              was deliberately absent and concluded it could not be done. The
              modal behind it still explains the bouncer half before the tap. */}
          <Show when={canDetach()}>
            <button
              type="button"
              class="shell-chrome-btn rail-action rail-action-detach"
              aria-label="switch account"
              data-testid="detach-btn"
              onClick={() => {
                confirmDetach(toLogin);
                close();
              }}
            >
              <span class="rail-action-icon" aria-hidden="true">
                {"\u{1F6AA}"}
              </span>
              <span class="rail-action-label">switch account</span>
            </button>
          </Show>

          {/* #986 — quit: close cic AND tear the live session down.
              Universal, and the ONE entry whose consequence is genuinely
              different per subject — a user parks and comes back, an anon
              visitor is deleted. `confirmQuit` picks the sentence that is
              true for whoever is looking at it. */}
          <button
            type="button"
            class="shell-chrome-btn rail-action rail-action-quit"
            aria-label="quit IRC"
            data-testid="quit-irc-btn"
            onClick={() => {
              confirmQuit(toLogin);
              close();
            }}
          >
            <span class="rail-action-icon" aria-hidden="true">
              {"\u{1F50C}"}
            </span>
            <span class="rail-action-label">quit</span>
          </button>
        </div>
      </Show>

      {/* #500 — the ONE permanently-pinned launcher. Always at the bottom of the
          rail, always reachable (it never shares vertical flow with the nick
          list). Toggles the overlay menu above.
          #1040 — absent on home, where the menu it would open is already the
          rail's content. A door is only an affordance from outside the room:
          rendered there it would be a permanent row whose only power is to
          COLLAPSE the column this issue exists to expand. */}
      <Show when={!expanded()}>
        <button
          type="button"
          class="shell-chrome-btn rail-action rail-actions-launcher"
          classList={{ open: open() }}
          aria-haspopup="menu"
          aria-expanded={open() ? "true" : "false"}
          aria-label="window actions"
          data-testid="rail-actions-launcher"
          onClick={() => setOpen((v) => !v)}
        >
          <span class="rail-action-icon" aria-hidden="true">
            {"\u{2630}"}
          </span>
          <span class="rail-action-label">actions</span>
        </button>
      </Show>
    </div>
  );
};

export default RailActions;
